import React, { Fragment } from 'react';
import classNames from 'classnames';
import AnimateHeight from 'react-animate-height';
import valuesAreEqual from 'lodash-es/isEqual';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Pagination, Button, ButtonRounded } from '../../../components/actions';
import { LoadingSpinner, LoadingIndicator } from '../../../components/loaders';
import { CenterContainer, Box, Separator } from '../../../components/containers';
import { ErrorBox, Headline } from '../../../components/text';
import { Calendar } from '../../../components/fields';
import SingleReport from './SingleReport';
import {
    printDateObjectAsAmericanDate,
    addDaysToDate,
    setSpecificMinuteOnDate,
    setSpecificHourOnDate,
} from '../../../helpers';
import { REPORTS_HISTORY_PAGINATION_LIMIT } from '../constants';
import {
    getReportsHistory,
    getReportsCategoriesList,
    setReportsHistoryReporTypeFilter,
    setReportsHistoryFromDateFilter,
    setReportsHistoryTilDateFilter,
    refreshLastReportsHistoryPage,
    toggleReportsHistoryManageMode,
} from '../action';
import {
    getIsHistoryLoading,
    getHasHistoryFetchFailed,
    getHistoryData,
    getHistoryTotal,
    getHistoryPage,
    getHistoryFilters,
    getIsReportsListLoading,
    getHasReportsListFetchFailed,
    getReportsList,
    getHistoryManageModeIsOn,
} from '../../../selectors';
import zenscroll from 'zenscroll';
import crossIcon from '../../../img/cross-icon.svg';
import s from './index.css';

const CrossButton = props => (
    <span>
        <img src={crossIcon} alt="" />
    </span>
);

const mapStateToProps = state => ({
    isReportsListLoading: getIsReportsListLoading(state),
    hasReportsListFetchFailed: getHasReportsListFetchFailed(state),
    reportsListData: getReportsList(state),
    loading: getIsHistoryLoading(state),
    error: getHasHistoryFetchFailed(state),
    count: getHistoryTotal(state),
    data: getHistoryData(state),
    page: getHistoryPage(state),
    filters: getHistoryFilters(state),
    manageModeIsOn: getHistoryManageModeIsOn(state),
});

const mapDispatchToProps = dispatch => ({
    dispatch,
    getReportsHistory: bindActionCreators(getReportsHistory, dispatch),
    refreshLastReportsHistoryPage: bindActionCreators(refreshLastReportsHistoryPage, dispatch),
    getReportsCategoriesList: bindActionCreators(getReportsCategoriesList, dispatch),
    setReportsHistoryReporTypeFilter: bindActionCreators(setReportsHistoryReporTypeFilter, dispatch),
    setReportsHistoryFromDateFilter: bindActionCreators(setReportsHistoryFromDateFilter, dispatch),
    setReportsHistoryTilDateFilter: bindActionCreators(setReportsHistoryTilDateFilter, dispatch),
    toggleReportsHistoryManageMode: bindActionCreators(toggleReportsHistoryManageMode, dispatch),
});

class ReportsHistory extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            midnight: this.calculateMidnightTomorrow(),
            areFiltersExpanded: false,
        };

        this.isReady = true;
        this.timer = null;

        this.handleMidnightDateRefresh();
    }

    componentWillMount() {
        this.props.getReportsHistory(1);

        // Every 30 seconds check if current reports page is missing any files
        setInterval(this.handleRefreshingIfPageHasReportsWithoutFiles, 1000 * 30);
    }

    componentWillReceiveProps(nextProps) {
        if (!valuesAreEqual(this.props.filters, nextProps.filters)) {
            this.props.getReportsHistory(1);
        }
    }

    componentWillUnmount() {
        this.isReady = false;

        clearInterval(this.handleRefreshingIfPageHasReportsWithoutFiles);
    }

    render() {
        const {
            isReportsListLoading,
            hasReportsListFetchFailed,
            reportsListData,
            loading,
            error,
            data,
            count,
            page,
            filters,
            manageModeIsOn,
        } = this.props;

        const noFilters = filters.reportType === null && filters.fromDate === null && filters.tilDate === null;

        const filteredByReportType =
            reportsListData && filters.reportType !== null
                ? reportsListData.find(r => r.reportId === filters.reportType) || null
                : null;

        return (
            <Fragment>
                {(loading || isReportsListLoading) &&
                    (!data || data.length <= 0) && (
                        <CenterContainer>
                            <LoadingSpinner size={64} />
                        </CenterContainer>
                    )}

                <div className={s.header}>
                    <Headline size="small">Reports history</Headline>

                    <LoadingIndicator label="Refreshing reports" show={loading} />

                    {(manageModeIsOn || (data && data.length > 0)) && (
                        <div>
                            <ButtonRounded
                                onClick={this.handleManageModeToggle}
                                design={manageModeIsOn ? 'warning' : 'default'}
                                label={manageModeIsOn ? 'Stop managing history' : 'Manage history'}
                            />
                        </div>
                    )}
                </div>

                <Separator type="narrow" />

                {error &&
                    loading === false && (
                        <ErrorBox
                            onClick={this.handleReportsHistoryRefetch}
                            label="Could not load history, click here to try again"
                        />
                    )}

                {hasReportsListFetchFailed &&
                    isReportsListLoading === false && (
                        <ErrorBox
                            onClick={this.handleRefetchReportTypes}
                            label="Could not load report types, click here to try again"
                        />
                    )}

                {data && (
                    <Fragment>
                        <Box className={s.filtersBox}>
                            <div className={s.filtersToolbar}>
                                <div className={s.filtersList}>
                                    <p>Filters:</p>

                                    {noFilters && <p className={s.noFilters}>show all reports</p>}

                                    {filters.reportType !== null && (
                                        <button onClick={this.handleFilterRemoval('reportType')}>
                                            {'Report type:'}
                                            <strong>
                                                {filteredByReportType
                                                    ? filteredByReportType.repTitle
                                                    : filters.reportType}
                                            </strong>
                                            <CrossButton />
                                        </button>
                                    )}

                                    {filters.fromDate !== null && (
                                        <button onClick={this.handleFilterRemoval('fromDate')}>
                                            {'From date'}
                                            <strong>{printDateObjectAsAmericanDate(filters.fromDate)}</strong>
                                            <CrossButton />
                                        </button>
                                    )}

                                    {filters.tilDate !== null && (
                                        <button onClick={this.handleFilterRemoval('tilDate')}>
                                            {'To date'}
                                            <strong>{printDateObjectAsAmericanDate(filters.tilDate)}</strong>
                                            <CrossButton />
                                        </button>
                                    )}
                                </div>

                                <div className={s.changeFilters}>
                                    <button
                                        onClick={this.handleFiltersToggle}
                                        className={classNames(s.changeFiltersButton, {
                                            [s.open]: this.state.areFiltersExpanded,
                                        })}
                                    >
                                        {this.state.areFiltersExpanded ? 'Close filters' : 'Change filters'}
                                    </button>
                                </div>
                            </div>

                            <AnimateHeight
                                component="div"
                                className={s.filtersActionsContainer}
                                height={this.state.areFiltersExpanded ? 'auto' : 0}
                                duration={500}
                            >
                                <div className={s.filtersActions}>
                                    <div>
                                        <Headline size="small">Report type</Headline>

                                        <div className={s.reportTypesList}>
                                            <button
                                                key="all"
                                                className={classNames({
                                                    [s.active]: filters.reportType === null,
                                                })}
                                                onClick={this.handleReportTypeFilterChange(null)}
                                            >
                                                {'Show all'}
                                                <span>1</span>
                                            </button>

                                            {reportsListData.map((report, reportIndex) => (
                                                <button
                                                    key={report.reportId}
                                                    className={classNames({
                                                        [s.active]: report.reportId === filters.reportType,
                                                    })}
                                                    onClick={this.handleReportTypeFilterChange(report.reportId)}
                                                >
                                                    {report.repTitle}
                                                    <span>{reportIndex + 2}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <Headline size="small">From date</Headline>

                                        <Calendar
                                            onChange={this.handleReportFromDateFilterChange}
                                            maxDate={this.state.midnight}
                                            value={filters.fromDate}
                                        />
                                    </div>

                                    <div>
                                        <Headline size="small">To date</Headline>

                                        <Calendar
                                            onChange={this.handleReportTilDateFilterChange}
                                            maxDate={this.state.midnight}
                                            value={filters.tilDate}
                                        />
                                    </div>
                                </div>
                            </AnimateHeight>
                        </Box>

                        <Separator />

                        {data &&
                            data.length > 0 && (
                                <Box className={s.reportsList} addShadow={true}>
                                    {data.map((report, reportIndex) => (
                                        <SingleReport
                                            key={report.reportRequestId}
                                            reportIndex={reportIndex}
                                            report={report}
                                            allReportsCount={data.length}
                                        />
                                    ))}
                                </Box>
                            )}

                        <Separator />
                    </Fragment>
                )}

                <Pagination
                    onPageChange={this.handlePageChange}
                    currentPage={page}
                    countPerPage={REPORTS_HISTORY_PAGINATION_LIMIT}
                    countTotal={count}
                    displayTotals={true}
                />
            </Fragment>
        );
    }

    handleManageModeToggle = e => {
        this.props.toggleReportsHistoryManageMode();
    };

    handlePageChange = page => {
        if (page !== this.props.page) {
            zenscroll.toY(0);
            this.props.getReportsHistory(page);
        }
    };

    handleReportsHistoryRefetch = e => {
        this.props.getReportsHistory(this.props.page);
    };

    handleRefetchReportTypes = e => {
        this.props.getReportsCategoriesList(true);
    };

    handleFilterRemoval = filterType => e => {
        if (filterType === 'reportType') {
            this.props.setReportsHistoryReporTypeFilter(null);
        } else if (filterType === 'fromDate') {
            this.props.setReportsHistoryFromDateFilter(null);
        } else if (filterType === 'tilDate') {
            this.props.setReportsHistoryTilDateFilter(null);
        }
    };

    handleFiltersToggle = e => {
        this.setState({
            areFiltersExpanded: !this.state.areFiltersExpanded,
        });
    };

    handleReportTypeFilterChange = reportId => e => {
        this.props.setReportsHistoryReporTypeFilter(reportId);
    };

    handleReportFromDateFilterChange = date => {
        this.props.setReportsHistoryFromDateFilter(date);
    };

    handleReportTilDateFilterChange = date => {
        this.props.setReportsHistoryTilDateFilter(date);
    };

    handleMidnightDateRefresh = () => {
        setTimeout(() => {
            if (this.isReady) {
                this.setState(
                    {
                        midnight: this.calculateMidnightTomorrow(),
                    },
                    () => {
                        this.handleMidnightDateRefresh();
                    }
                );
            }
        }, 1000 * 60 * 5);
    };

    handleRefreshingIfPageHasReportsWithoutFiles = () => {
        if (
            this.isReady &&
            this.props.manageModeIsOn === false &&
            this.checkIfAnyDisplayedReportIsWithoutFile(this.props.data)
        ) {
            this.props.refreshLastReportsHistoryPage();
        }
    };

    checkIfAnyDisplayedReportIsWithoutFile = data => {
        return data && data.length > 0 && data.filter(report => report.status === 0).length > 0;
    };

    calculateMidnightTomorrow = () => {
        return setSpecificMinuteOnDate(setSpecificHourOnDate(addDaysToDate(new Date(), 1), 0), 0);
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ReportsHistory);



// WEBPACK FOOTER //
// ./app/web/src/container/ReportsPage/History/index.jsx